import React, { useState, useRef, useEffect, useMemo } from 'react';
import { DIAL_CODES, parsePhone, combinePhone } from '../utils/phoneCodes';

// A contact-number field with a country dial-code selector next to it.
// Controlled: `value` is the full stored string ("+91 98765 43210" or a bare
// legacy number), and `onChange(newValue)` receives the recombined string.
// The parent keeps storing a single string field — no schema/state change.
//
// The code picker is a custom dropdown (not a native <select>) so that:
//   • collapsed, it shows just the flag + dial code (e.g. "🇬🇷 +30") — compact.
//   • open, each row shows flag + code + COUNTRY NAME so you can pick correctly,
//     with a search box to filter the ~240 countries.
const PhoneInput = ({ value = '', onChange, placeholder = 'Contact number', disabled = false }) => {
  const { code, number } = parsePhone(value);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  const emit = (nextCode, nextNumber) => {
    if (onChange) onChange(combinePhone(nextCode, nextNumber));
  };

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  // Focus the search box when the menu opens.
  useEffect(() => { if (open && searchRef.current) searchRef.current.focus(); }, [open]);

  const selected = useMemo(() => DIAL_CODES.find(c => c.code === code) || null, [code]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return DIAL_CODES;
    return DIAL_CODES.filter(c =>
      c.name.toLowerCase().includes(q) || c.code.includes(q) || c.code.replace('+', '').includes(q)
    );
  }, [query]);

  return (
    <div className="flex gap-2">
      <div className="relative flex-shrink-0" ref={wrapRef}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(o => !o)}
          className="w-24 h-full flex items-center justify-between gap-1 border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white hover:bg-gray-50 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent disabled:bg-gray-100"
          title={selected ? `${selected.name} (${selected.code})` : 'Select country code'}
        >
          <span className="truncate">
            {selected ? `${selected.flag} ${selected.code}` : <span className="text-gray-400">Code</span>}
          </span>
          <svg className={`w-3 h-3 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </button>

        {open && (
          <div className="absolute z-50 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg">
            <div className="p-2 border-b border-gray-100">
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country or code…"
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
              />
            </div>
            <ul className="max-h-60 overflow-y-auto py-1">
              {code && (
                <li>
                  <button type="button" onClick={() => { emit('', number); setOpen(false); setQuery(''); }}
                    className="w-full text-left px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-50">
                    Clear code
                  </button>
                </li>
              )}
              {filtered.length === 0 ? (
                <li className="px-3 py-2 text-sm text-gray-400">No match</li>
              ) : filtered.map(c => (
                <li key={c.iso}>
                  <button
                    type="button"
                    onClick={() => { emit(c.code, number); setOpen(false); setQuery(''); }}
                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2 ${c.code === code ? 'bg-[#e1f0da] font-medium' : ''}`}
                  >
                    <span className="w-6 flex-shrink-0">{c.flag}</span>
                    <span className="w-12 flex-shrink-0 text-gray-600">{c.code}</span>
                    <span className="truncate">{c.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <input
        type="tel"
        value={number}
        disabled={disabled}
        onChange={(e) => emit(code, e.target.value)}
        placeholder={placeholder}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
      />
    </div>
  );
};

export default PhoneInput;
